import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Box, Button } from '@material-ui/core';
import { IoClose } from 'react-icons/io5';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { FiMessageCircle } from 'react-icons/fi';

import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import {
  AppA,
  AppDataGrid,
  AppIconButton,
  GridCellWithAvatar,
  ButtonTitleWithLoading,
  ConfirmDialog,
} from '@vyce/core/src/components';
import {
  getFormParticipantsRequest,
  removeFormParticipantRequest,
  sendFormMessageRequest,
} from '@vyce/core/src/api/forms';
import { ChatUser, FormParticipant, User } from '@vyce/core/src/types';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

const SORTABLE_FIELDS = ['name', 'email', 'phone', 'status', 'invitedAt'] as const;

interface Props {
  companyId: string;
  formId: string;
  user: User;
  type?: 'recipients' | 'responses' | 'outstanding';
  /** Bump this value to force the list to reload (e.g. after sending a new invite). */
  reloadKey?: number;
  height?: string;
  /** When the search field lives outside the table, pass its value here. */
  searchValue?: string;
  /** Hide the table's own search input (e.g. when a toolbar owns the search). */
  hideSearchInput?: boolean;
}

export const FormRecipientsTable: React.FC<Props> = ({
  companyId,
  formId,
  user,
  type = 'recipients',
  reloadKey = 0,
  height = 'calc(50vh)',
  searchValue,
  hideSearchInput = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<FormParticipant[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const activeSearch = hideSearchInput ? searchValue ?? '' : searchInput;
  const debouncedSearch = useDebounceValue(activeSearch, 300);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const { createPrivateChat } = useChatRequests();
  const [loadingResendId, setLoadingResendId] = useState<string | null>(null);
  const { showNotification } = useContext(NotificationContext);
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = useState(false);
  const [loadingRemoveUser, setLoadingRemoveUser] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);

  const chatCreator: ChatUser = useMemo(
    () => ({
      uid: user.uuid,
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      phone: user.phone,
      avatarUrl: user.photo?.url,
    }),
    [user.uuid, user.first_name, user.last_name, user.email, user.phone, user.photo?.url]
  );

  const goToChat = useCallback(
    async (participant: FormParticipant) => {
      await createPrivateChat({ creator: chatCreator, chatUser: participant, companyId });
    },
    [createPrivateChat, companyId, chatCreator]
  );

  const loadParticipants = useCallback(async () => {
    if (!companyId || !formId || !user.uuid) return;
    try {
      setLoading(true);
      let status: string | null = null;
      if (type === 'responses') {
        status = 'completed';
      } else if (type === 'outstanding') {
        status = 'invited';
      }

      const rawField = sortModel[0]?.field;
      const sortField: (typeof SORTABLE_FIELDS)[number] = SORTABLE_FIELDS.includes(
        rawField as (typeof SORTABLE_FIELDS)[number]
      )
        ? (rawField as (typeof SORTABLE_FIELDS)[number])
        : 'name';
      const sortDirection = sortModel[0]?.sort === 'desc' ? 'desc' : 'asc';

      const searchForApi = debouncedSearch === null ? '' : debouncedSearch.trim();

      const response = await getFormParticipantsRequest({
        companyId,
        formId,
        userId: user.uuid,
        status,
        search: searchForApi || null,
        limit: GRID_PAGE_SIZE,
        offset: page * GRID_PAGE_SIZE,
        sortField,
        sortDirection,
      });
      setUsers(
        (response.participants as (FormParticipant & { id: string })[]).map(participant => ({
          ...participant,
          id: participant.id,
        }))
      );
      setTotal(typeof response.total === 'number' ? response.total : 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, formId, user.uuid, type, page, debouncedSearch, sortModel]);

  const handleResend = useCallback(
    async (participantId: string) => {
      if (!companyId || !formId || !user.uuid) return;
      try {
        setLoadingResendId(participantId);
        await sendFormMessageRequest({
          companyId,
          formId,
          userId: user.uuid,
          sendToAll: false,
          participantIds: [participantId],
        });
        showNotification({ message: 'Form has been sent', options: { variant: 'success' } });
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingResendId(null);
      }
    },
    [companyId, formId, user.uuid, showNotification]
  );

  const handleRemove = async (participantId: string) => {
    if (!companyId || !formId || !user.uuid) return;
    try {
      setLoadingRemoveUser(true);
      await removeFormParticipantRequest({ companyId, formId, userId: user.uuid, participantId });
      showNotification({ message: 'User has been removed', options: { variant: 'success' } });
      loadParticipants();
      setOpenRemoveUserDialog(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingRemoveUser(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        minWidth: 120,
        disableColumnMenu: true,
        renderCell: params => (
          <GridCellWithAvatar
            avatarUrl={params.row.avatarUrl}
            avatarPlaceholder={getAvatar()}
            name={params.row.name}
            createPrivateChat={params.row?.uid ? () => goToChat(params.row as FormParticipant) : undefined}
          />
        ),
      },
      {
        field: 'email',
        headerName: 'Email #',
        flex: 1.5,
        minWidth: 180,
        disableColumnMenu: true,
        renderCell: params => (
          <AppA cy-test-id="user-email" content={params.row.email} href={`mailto: ${params.row.email}`} />
        ),
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        disableColumnMenu: true,
        minWidth: 180,
        renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        disableColumnMenu: true,
        minWidth: 180,
        renderCell: params => <Box>{params.row.status}</Box>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box display="flex" width="100%" alignItems="center" justifyContent="flex-end" gridGap={8}>
            {params.row.status !== 'completed' && (
              <>
                <Button
                  size="small"
                  color="primary"
                  style={{ width: 100 }}
                  startIcon={<FiMessageCircle size={14} />}
                  onClick={() => handleResend(params.row.id ?? '')}
                  variant="contained"
                  disabled={loadingResendId === params.row.id}
                >
                  <ButtonTitleWithLoading
                    title="Resend"
                    loaderVariant="paper"
                    loading={loadingResendId === params.row.id}
                    size={14}
                  />
                </Button>

                <AppIconButton
                  variant="error"
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  onClick={() => {
                    setSelectedParticipantId(params.row.id ?? '');
                    setOpenRemoveUserDialog(true);
                  }}
                >
                  <IoClose size="20px" />
                </AppIconButton>
              </>
            )}
          </Box>
        ),
      },
    ],
    [loadingResendId, goToChat, handleResend]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const next: GridSortModel = model.length ? model : [{ field: 'name', sort: 'asc' }];
      const prev = sortModel[0];
      const n = next[0];
      const sameField = (prev?.field ?? 'name') === (n?.field ?? 'name');
      const sameOrder = (prev?.sort ?? 'asc') === (n?.sort ?? 'asc');
      if (sameField && sameOrder) {
        return;
      }
      setSortModel(next);
      setPage(0);
    },
    [sortModel]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(p => (p === newPage ? p : newPage));
  }, []);

  const sortFieldDep = sortModel[0]?.field ?? 'name';
  const sortOrderDep = sortModel[0]?.sort ?? 'asc';
  const debouncedSearchDep = debouncedSearch ?? '';

  useEffect(() => {
    void loadParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, formId, user.uuid, type, page, debouncedSearchDep, sortFieldDep, sortOrderDep, reloadKey]);

  useEffect(() => {
    setPage(p => (p !== 0 ? 0 : p));
  }, [debouncedSearchDep]);

  return (
    <>
      {!hideSearchInput && (
        <Box marginBottom={2}>
          <AppSearchInput
            placeholder="Search"
            onChange={e => setSearchInput(e.target.value)}
            value={searchInput}
            isSmall
            smallRounding
            isBorder
            expanded={true}
          />
        </Box>
      )}
      <AppDataGrid
        noPaper={true}
        rows={users}
        getRowId={row => row.id}
        columns={columns}
        height={height}
        rowCount={total}
        loading={loading}
        paginationMode="server"
        page={page}
        pageSize={GRID_PAGE_SIZE}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        onPageChange={handlePageChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableSelectionOnClick
      />

      <ConfirmDialog
        open={openRemoveUserDialog}
        handleClose={() => setOpenRemoveUserDialog(false)}
        handleConfirm={() => handleRemove(selectedParticipantId || '')}
        title="Remove from Form"
        subtitle="Are you sure? Selected team member will lose access to this Form."
        loading={loadingRemoveUser}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
};
